import React, { useState, FormEvent, ChangeEvent } from 'react';
import { Images } from '../../constants';
import { useTranslation } from 'react-i18next';
import { FamlaServices } from '../../services';
import { useAppSelector } from '../../stores';
import { useNavigate, useParams } from 'react-router-dom';
import { Routes } from '../../routes';
import { iAlert } from '../../interfaces';
import { Alert } from '../../components';
import { isPrivateEmail } from '../../utils/emailUtils';
import { fetchOutlookPromo } from '../../services/api/outlookPromoService';
import '../../styles/modern-theme.css';
import './invite-teammate-page.css';

const InvitePage: React.FC = () => {
    const { t } = useTranslation();
    const { from } = useParams();
    const users = useAppSelector(state => state.auth);
    const language = localStorage.getItem('currentLanguage') === "fr" ? "french" : "english"
    const navigatorLang = navigator.language.split('-')[0] === "fr" ? "french" : "english";
    const [emailInput, setEmailInput] = useState<string>('');
    const [emails, setEmails] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [alert, setAlert] = useState<iAlert>({ isActive: false, type: '', message: '' });
    const conditionnalRoutes = from === "home" ? Routes.HOMEVIEW : Routes.REFERRAL;

    /** Après invitation du funnel auth → étape Outlook promo si éligible, sinon referral. */
    const continueAfterInviteFunnel = async () => {
        if (from === 'home') {
            navigate(Routes.HOMEVIEW);
            return;
        }
        try {
            const promo = await fetchOutlookPromo(users.access_token);
            if (promo.eligible && promo.pending?.includes('onboarding')) {
                navigate(Routes.COMPANY_OUTLOOK_PROMO, { replace: true });
                return;
            }
        } catch {
            /* fall through */
        }
        navigate(Routes.REFERRAL, { replace: true });
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEmailInput(e.target.value);
        // Réinitialiser l'alerte quand l'utilisateur modifie l'email
        if (alert.isActive) {
            setAlert({ isActive: false, type: '', message: '' });
        }
    };

    const handleAddEmail = () => {
        const email = emailInput.trim();
        if (email && !emails.includes(email) && validateEmail(email)) {
            // Vérifier si l'email est professionnel
            if (isPrivateEmail(email)) {
                setAlert({ isActive: true, type: 'warning', message: t('views.auth.professionalEmail') });
                return;
            }
            setEmails((prev) => [...prev, email]);
            setEmailInput('');
            // Réinitialiser l'alerte après ajout réussi
            setAlert({ isActive: false, type: '', message: '' });
        }
    };

    const validateEmail = (email: string): boolean => {
        const re = /\S+@\S+\.\S+/;
        return re.test(email);
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        setEmails(emails.filter((email) => email !== emailToRemove));
    };


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            setLoading(true);
            const validEmails = emails.filter(email => email.trim() !== '');
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const invalidEmails = validEmails.filter(email => !emailRegex.test(email));

            if (invalidEmails.length > 0) {
                setAlert({ isActive: true, type: 'danger', message: t('navigation.enterValidEmails') });
                setLoading(false);
                return;
            }

            // Vérifier les emails privés (non professionnels)
            const privateEmails = validEmails.filter(email => isPrivateEmail(email));
            if (privateEmails.length > 0) {
                setAlert({ isActive: true, type: 'warning', message: t('views.auth.professionalEmail') });
                setLoading(false);
                return;
            }

            if (validEmails.length === 0) {
                // setAlert({ isActive: true, type: 'danger', message: t('navigation.enterAtLeastOneEmail') });
                await continueAfterInviteFunnel();
                setLoading(false);
                return;
            }
            const formData = {
                mail: emails,
                lang: language || navigatorLang
            }
            
            console.log('[InviteTeammateView] Sending invitation request:', { emails, formData });
            const response = await FamlaServices("api_host", `/process/add/worker/`, "PUT", formData, users.access_token);
            console.log('[InviteTeammateView] Response received:', { status: response.status, body: response.body });
            
            if (response.status === 200) {
                setEmails([]);
                setEmailInput('');
                setAlert({ isActive: true, type: 'success', message: t('navigation.invitationSentSuccessfully', 'Invitations envoyées avec succès') });
                setTimeout(() => {
                    void continueAfterInviteFunnel();
                }, 1500);
            } else {
                // Gérer les erreurs de l'API
                const errorMessage = response.body?.description || response.body?.message || t('navigation.anErrorOcured', 'Une erreur s\'est produite, veuillez réessayer plus tard');
                setAlert({ isActive: true, type: 'danger', message: errorMessage });
                console.error('[InviteTeammateView] API Error:', { status: response.status, body: response.body });
            }

        } catch (error: any) {
            console.error('[InviteTeammateView] Exception caught:', error);
            const errorMessage = error?.message || t('navigation.anErrorOcured', 'Une erreur s\'est produite, veuillez réessayer plus tard');
            setAlert({ isActive: true, type: 'danger', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="invite-teammate-page d-flex justify-content-center min-vh-100 overflow-hidden py-5">
            <div className="row d-flex justify-content-center align-items-center g-5 px-3 ">
                {/* Form block */}
                <div className="col-md-6 col-12 d-flex justify-content-center">
                    <div className="w-100" style={{ maxWidth: 500 }}>

                        <div className="mb-4">
                            <p className="invite-teammate-page__tip-line">
                                <strong>{t('navigation.proTip')}:</strong> {t('navigation.textProtip')}
                            </p>
                            <p className="invite-teammate-page__lead my-2">
                                {t('navigation.proTipSmalltext')}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <h1 className="invite-teammate-page__title fs-3 fw-bold my-3">{t('navigation.inviteYourTeammates')}</h1>

                            <div className="input-group mb-4">
                                <input
                                    type="email"
                                    className="form-control invite-teammate-page__input"
                                    placeholder={t('views.auth.emailAddresPro')}
                                    value={emailInput}
                                    onChange={handleEmailChange}
                                />
                                <button type="button" className="btn btn-theme" onClick={handleAddEmail}>
                                    {t("words.add")}
                                </button>
                            </div>

                            {emails.length > 0 && (
                                <div className="mb-3">
                                    {emails.map((email) => (
                                        <div
                                            key={email}
                                            className="invite-teammate-page__chip d-flex justify-content-between align-items-center p-2 rounded mb-2"
                                        >
                                            <span>{email}</span>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => handleRemoveEmail(email)}
                                                aria-label="Remove"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {alert.isActive &&
                                < Alert type={alert.type} msg={alert.message} clasName='mb-3' />
                            }

                            <button 
                                type="submit" 
                                className="btn btn-theme w-100" 
                                disabled={emails.length === 0 || loading}
                            >
                                {loading ? t("navigation.sendInvitations") + '...' : t("navigation.sendInvitations")}
                            </button>
                            <button
                                type="button"
                                className="btn invite-teammate-page__back w-100 mt-3"
                                onClick={() => {
                                    if (from === 'home') {
                                        navigate(conditionnalRoutes);
                                    } else {
                                        void continueAfterInviteFunnel();
                                    }
                                }}
                            >
                                <i className="fa fa-arrow-left me-2 mt-2"></i>{from === 'home' ? t('words.back') : t("navigation.skipThisStep")}

                            </button>
                        </form>
                    </div>
                </div>


                {/* Image block */}
                <div className="col-md-6 col-12">
                    <div
                        className="position-relative w-100 rounded overflow-hidden d-flex align-items-center justify-content-center"
                        style={{ height: '400px', maxHeight: '700px' }}
                    >
                        {/* Image floutée en arrière-plan */}
                        <div
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{
                                backgroundImage: `url(${Images.map})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                filter: 'blur(12px)',
                                transform: 'scale(1.1)',
                                zIndex: 1,
                            }}
                        ></div>

                        {/* Image nette au premier plan */}
                        <img
                            src={Images.map}
                            alt="Invite illustration"
                            className="position-relative img-fluid rounded shadow-lg z-2"
                            style={{
                                maxHeight: '90%',
                                width: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default InvitePage;
